import React, { useState, useCallback, useMemo } from "react";
import "../../styles/doctorsview.css";

import DateNavigator from "./Datenavigator";
import VitalChart    from "./Vitalchart";
import NcdSummary    from "./Ncdsummary";

import {
  VITALS,
  searchPatientByPhone,
  fetchFullProgram,
  fetchTrendsAllPages,
  processWearable,
  extractSleepItems,
} from "./Doctorviewutils";

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function latestISTAnchor(sleepItems) {
  if (!sleepItems?.length) return new Date();
  const latestTs = Math.max(...sleepItems.map(i => (i.ts < 1e12 ? i.ts * 1000 : i.ts)));
  const d = new Date(latestTs + IST_OFFSET_MS);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export default function DoctorView() {
  const [phone,      setPhone]      = useState("");
  const [searching,  setSearching]  = useState(false);
  const [loadTrends, setLoadTrends] = useState(false);
  const [error,      setError]      = useState("");
  const [patient,    setPatient]    = useState(null);
  const [trends,     setTrends]     = useState(null);
  const [activeVital,setActiveVital]= useState(null);

  const [rawWearable,      setRawWearable]      = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedSource,   setSelectedSource]   = useState(null);

  const [viewMode, setViewMode] = useState("week");
  const [anchor,   setAnchor]   = useState(new Date());

  /* rawSleepItems filtered to selectedSource */
  const rawSleepItems = useMemo(() => {
    if (!selectedSource || !rawWearable.length) return [];
    return extractSleepItems(rawWearable.filter(x => x.source === selectedSource));
  }, [rawWearable, selectedSource]);

  /* ── Search ── */
  const handleSearch = useCallback(async () => {
    const num = phone.trim();
    if (!num || num.length < 6) { setError("Please enter a valid phone number."); return; }
    setError(""); setPatient(null); setTrends(null); setActiveVital(null);
    setRawWearable([]); setAvailableSources([]); setSelectedSource(null);
    setSearching(true);
    try {
      const found = await searchPatientByPhone(num);
      if (!found) { setError("No patient found with that phone number."); return; }
      const full = await fetchFullProgram(found.patient_id);
      setPatient(full || found);
    } catch { setError("Search failed. Please try again."); }
    finally  { setSearching(false); }
  }, [phone]);

  /* ── Load trends ── */
  const handleLoadTrends = useCallback(async () => {
    if (!patient?.patient_id) return;
    setLoadTrends(true);
    try {
      const raw      = await fetchTrendsAllPages(patient.patient_id);
      const wearable = raw.wearable || [];
      const sources  = [...new Set(wearable.map(x => x.source).filter(Boolean))];
      const defaultSrc = sources.includes("jcband") ? "jcband"
                       : sources.includes("qring")  ? "qring"
                       : sources[0] || null;

      setRawWearable(wearable);
      setAvailableSources(sources);
      setSelectedSource(defaultSrc);
      setTrends(processWearable(wearable, defaultSrc));
      setActiveVital("heart_rate");

      // Auto-anchor to latest IST date with sleep data for the default source
      const sleepForDefault = extractSleepItems(wearable.filter(x => x.source === defaultSrc));
      if (sleepForDefault.length > 0) setAnchor(latestISTAnchor(sleepForDefault));
    } catch { setError("Failed to load health trends."); }
    finally  { setLoadTrends(false); }
  }, [patient]);

  /* ── Switch source ── */
  const handleSourceChange = useCallback((src) => {
    setSelectedSource(src);
    setTrends(processWearable(rawWearable, src));
    // Re-anchor to latest sleep date for this source
    const sleepForSrc = extractSleepItems(rawWearable.filter(x => x.source === src));
    if (sleepForSrc.length > 0) setAnchor(latestISTAnchor(sleepForSrc));
  }, [rawWearable]);

  const profile = patient?.user_profile;
  const ncd     = patient?.ncd_data;
  const sourceLabel = s => s === "jcband" ? "JC Band" : s === "qring" ? "Q Ring" : s;

  return (
    <div className="dv-root">

      {/* ── Search ── */}
      <div className="dv-search-wrap">
        <div className="dv-search-label">Enter Patient Phone Number</div>
        <div className="dv-search-row">
          <input className="dv-search-input" type="tel" placeholder="e.g. 9876543210"
            value={phone} onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key==="Enter" && handleSearch()} autoFocus/>
          <button className="dv-search-btn" onClick={handleSearch} disabled={searching}>
            {searching ? <span className="dv-spin"/> : "🔍"} Search
          </button>
        </div>
        {error && <div className="dv-error">{error}</div>}
      </div>

      {patient && (
        <div className="dv-result">

          {/* Profile */}
          <div className="dv-profile-header">
            <div className="dv-avatar">{profile?.name?.[0]?.toUpperCase() || "P"}</div>
            <div className="dv-profile-info">
              <div className="dv-profile-name">{profile?.name || "Patient"}</div>
              <div className="dv-profile-meta">
                {profile?.age      && <span>{profile.age} yrs</span>}
                {profile?.gender   && <span>{profile.gender}</span>}
                {profile?.weight   && <span>{profile.weight} kg</span>}
                {profile?.height   && <span>{profile.height} cm</span>}
                {profile?.phone_no && <span>📞 {profile.phone_no}</span>}
              </div>
              <div className="dv-profile-program">
                Program: <strong>{(patient.program_id||"—").toUpperCase()}</strong>
                &nbsp;·&nbsp; Phase: <strong>{patient.current_phase||"—"}</strong>
              </div>
            </div>
          </div>

          {/* Clinical overview */}
          {ncd && (
            <div className="dv-section">
              <div className="dv-section-title">🏥 Clinical Overview</div>
              <NcdSummary ncd={ncd}/>
              {Object.entries(ncd.risks||{}).filter(([,v])=>v).length > 0 && (
                <div className="dv-risk-row">
                  {Object.entries(ncd.risks).filter(([,v])=>v).map(([k]) => (
                    <span key={k} className="dv-risk-chip">⚠ {k.replace(/_/g," ")}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Medical reports */}
          {patient.medical_assets?.length > 0 && (
            <div className="dv-section">
              <div className="dv-section-title">📎 Medical Reports</div>
              <div className="dv-assets-list">
                {patient.medical_assets.map((a,i) => (
                  <a key={i} href={a.url||a.link||a} target="_blank" rel="noreferrer" className="dv-asset-link">
                    📄 Report {i+1}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Health trends */}
          <div className="dv-section">
            <div className="dv-trends-header">
              <div className="dv-section-title" style={{marginBottom:0}}>📊 Health Trends</div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>

                {/* Source toggle — only when multiple sources AND trends loaded */}
                {trends && availableSources.length > 1 && (
                  <div className="source-toggle">
                    {availableSources.map(src => (
                      <button key={src}
                        className={selectedSource===src ? "active" : ""}
                        onClick={() => handleSourceChange(src)}>
                        {sourceLabel(src)}
                      </button>
                    ))}
                  </div>
                )}

                {!trends && (
                  <button className="dv-load-trends-btn" onClick={handleLoadTrends} disabled={loadTrends}>
                    {loadTrends ? <><span className="dv-spin"/> Loading…</> : "Load Health Data"}
                  </button>
                )}
              </div>
            </div>

            {trends && (
              <>
                <div className="dv-vital-tabs">
                  {VITALS.map(v => (
                    <button key={v.key}
                      className={`dv-vital-tab ${activeVital===v.key?"active":""}`}
                      style={activeVital===v.key
                        ? {borderColor:v.color,color:v.color,background:`${v.color}18`} : {}}
                      onClick={() => setActiveVital(v.key)}>
                      {v.label}
                    </button>
                  ))}
                </div>

                {activeVital && (
                  <DateNavigator
                    viewMode={viewMode} setViewMode={setViewMode}
                    anchor={anchor}    setAnchor={setAnchor}
                  />
                )}

                {activeVital && (
                  <VitalChart
                    config={VITALS.find(v=>v.key===activeVital)}
                    rawData={trends[activeVital]}
                    rawSleepItems={rawSleepItems}
                    viewMode={viewMode}
                    anchor={anchor}
                    setAnchor={setAnchor}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!patient && !searching && !error && (
        <div className="dv-empty-state">
          <div className="dv-empty-icon">👨‍⚕️</div>
          <div className="dv-empty-title">Doctor Patient Lookup</div>
          <div className="dv-empty-sub">
            Type the patient's phone number above to instantly pull up their health profile,
            programme progress, and vital trends.
          </div>
        </div>
      )}
    </div>
  );
}